import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { BookOpen, Check, X, Loader2, Eye, Library } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { COST_CATEGORIES, COST_TRADES, UNITS_METRIC } from "@shared/schema";

type GapStatus = "pending" | "approved" | "dismissed";

type ScopeVocabularyGap = {
  id: string;
  generatedQuoteLogId: string | null;
  userId: string | null;
  market: string | null;
  phrase: string;
  context: string | null;
  status: GapStatus;
  adminNote: string | null;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  createdAt: string;
};

type ListResponse = {
  gaps: ScopeVocabularyGap[];
  total: number;
  pendingCount: number;
};

type CostLibraryFormState = {
  name: string;
  category: string;
  trade: string;
  unit: string;
  rateLow: string;
  rateTypical: string;
  rateHigh: string;
};

function statusBadgeClass(status: GapStatus): string {
  if (status === "approved") return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
  if (status === "dismissed") return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200";
  return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("en-GB");
}

function isPositiveNumber(v: string): boolean {
  const n = parseFloat(v);
  return !isNaN(n) && n > 0;
}

function ReviewDialog({
  gap,
  action,
  onClose,
}: {
  gap: ScopeVocabularyGap | null;
  action: "approved" | "dismissed" | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [addToCostLib, setAddToCostLib] = useState(false);
  const [clForm, setClForm] = useState<CostLibraryFormState>({
    name: "",
    category: "",
    trade: "",
    unit: "",
    rateLow: "",
    rateTypical: "",
    rateHigh: "",
  });

  const tradesForCategory = clForm.category
    ? (COST_TRADES[clForm.category]?.trades ?? [])
    : [];

  const unitEntries = Object.entries(UNITS_METRIC);

  function updateClForm(field: keyof CostLibraryFormState, value: string) {
    setClForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "category" ? { trade: "" } : {}),
    }));
  }

  function clFormValid(): boolean {
    if (!addToCostLib) return true;
    return (
      clForm.name.trim().length > 0 &&
      clForm.category.length > 0 &&
      clForm.trade.length > 0 &&
      clForm.unit.length > 0 &&
      isPositiveNumber(clForm.rateLow) &&
      isPositiveNumber(clForm.rateTypical) &&
      isPositiveNumber(clForm.rateHigh)
    );
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!gap || !action) return;

      const res = await apiRequest("PATCH", `/api/admin/vocabulary-gaps/${gap.id}`, {
        status: action,
        adminNote: note.trim() || null,
      });
      await res.json();

      if (addToCostLib && action === "approved") {
        try {
          await apiRequest("POST", "/api/admin/cost-library/default", {
            name: clForm.name.trim(),
            category: clForm.category,
            trade: clForm.trade,
            unit: clForm.unit,
            rateLow: clForm.rateLow,
            rateTypical: clForm.rateTypical,
            rateHigh: clForm.rateHigh,
            market: "uk",
          });
        } catch {
          throw new Error("cost_library_failed");
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vocabulary-gaps"] });
      const msg =
        action === "approved" && addToCostLib
          ? "Approved and added to cost library"
          : action === "approved"
          ? "Approved"
          : "Dismissed";
      toast({ title: msg });
      setNote("");
      setAddToCostLib(false);
      setClForm({ name: "", category: "", trade: "", unit: "", rateLow: "", rateTypical: "", rateHigh: "" });
      onClose();
    },
    onError: (err) => {
      if (err instanceof Error && err.message === "cost_library_failed") {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/vocabulary-gaps"] });
        toast({
          title: "Approved",
          description: "The gap was approved, but the cost library item could not be saved. You can add it manually from the Cost Library page.",
          variant: "destructive",
        });
        setNote("");
        setAddToCostLib(false);
        setClForm({ name: "", category: "", trade: "", unit: "", rateLow: "", rateTypical: "", rateHigh: "" });
        onClose();
        return;
      }
      toast({
        title: "Update failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const open = !!gap && !!action;

  function handleOpen(next: boolean) {
    if (!next) {
      setNote("");
      setAddToCostLib(false);
      setClForm({ name: "", category: "", trade: "", unit: "", rateLow: "", rateTypical: "", rateHigh: "" });
      onClose();
    }
  }

  function handleApproveToggle() {
    const nextVal = !addToCostLib;
    setAddToCostLib(nextVal);
    if (nextVal && gap) {
      setClForm((prev) => ({ ...prev, name: gap.phrase }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{action === "approved" ? "Approve vocabulary gap" : "Dismiss vocabulary gap"}</DialogTitle>
          <DialogDescription>
            {action === "approved"
              ? "Confirms this phrase reveals a real gap in the canonical item list. Use the toggle below to immediately add a new default cost library entry seeded from this phrase."
              : "Confirms this phrase does not need a new canonical item (e.g. it was already covered, or too vague to act on)."}
          </DialogDescription>
        </DialogHeader>
        {gap && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
            <p className="font-medium">"{gap.phrase}"</p>
            {gap.context && <p className="text-muted-foreground">{gap.context}</p>}
          </div>
        )}

        {action === "approved" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="add-to-cl"
                checked={addToCostLib}
                onCheckedChange={handleApproveToggle}
              />
              <Label htmlFor="add-to-cl" className="text-sm font-medium flex items-center gap-1.5 cursor-pointer">
                <Library className="h-3.5 w-3.5" /> Also add to cost library
              </Label>
            </div>

            {addToCostLib && (
              <div className="rounded-md border p-3 space-y-3 bg-muted/20">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={clForm.name}
                    onChange={(e) => updateClForm("name", e.target.value)}
                    placeholder="e.g. Install steel beam"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Category</Label>
                    <Select value={clForm.category} onValueChange={(v) => updateClForm("category", v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {COST_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value} className="text-xs">
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Trade</Label>
                    <Select
                      value={clForm.trade}
                      onValueChange={(v) => updateClForm("trade", v)}
                      disabled={tradesForCategory.length === 0}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={tradesForCategory.length === 0 ? "Pick category first" : "Select trade"} />
                      </SelectTrigger>
                      <SelectContent>
                        {tradesForCategory.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="text-xs">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Select value={clForm.unit} onValueChange={(v) => updateClForm("unit", v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitEntries.map(([value, label]) => (
                        <SelectItem key={value} value={value} className="text-xs">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Rate Low (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={clForm.rateLow}
                      onChange={(e) => updateClForm("rateLow", e.target.value)}
                      className="h-8 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rate Typical (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={clForm.rateTypical}
                      onChange={(e) => updateClForm("rateTypical", e.target.value)}
                      className="h-8 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rate High (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={clForm.rateHigh}
                      onChange={(e) => updateClForm("rateHigh", e.target.value)}
                      className="h-8 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={action === "approved" ? "Optional admin note" : "Optional note"}
          className="min-h-[70px] text-sm"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !clFormValid()}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : action === "approved" ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
            {action === "approved" ? "Approve" : "Dismiss"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GapDetailDialog({ gap, onClose }: { gap: ScopeVocabularyGap | null; onClose: () => void }) {
  return (
    <Dialog open={!!gap} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Phrase Detail
          </DialogTitle>
          <DialogDescription>Full phrase and context from the engine self-report</DialogDescription>
        </DialogHeader>
        {gap && (
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">Phrase</p>
              <p className="rounded-md border bg-muted/30 p-3 leading-relaxed">{gap.phrase}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">Context</p>
              <p className="rounded-md border bg-muted/30 p-3 leading-relaxed whitespace-pre-wrap">
                {gap.context ?? <span className="italic text-muted-foreground">No context provided</span>}
              </p>
            </div>
            <div className="flex gap-6 text-xs text-muted-foreground">
              <span><span className="font-medium">Market:</span> {gap.market ? gap.market.toUpperCase() : "—"}</span>
              <span><span className="font-medium">Logged:</span> {formatDateTime(gap.createdAt)}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminVocabularyGapsPage() {
  const [statusFilter, setStatusFilter] = useState<"all" | GapStatus>("pending");
  const [reviewTarget, setReviewTarget] = useState<{ gap: ScopeVocabularyGap; action: "approved" | "dismissed" } | null>(null);
  const [viewGap, setViewGap] = useState<ScopeVocabularyGap | null>(null);

  const params = new URLSearchParams();
  if (statusFilter !== "all") params.set("status", statusFilter);
  const queryString = params.toString();

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: [`/api/admin/vocabulary-gaps${queryString ? `?${queryString}` : ""}`],
  });

  const gaps = data?.gaps ?? [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold">Vocabulary Gaps</h1>
        <p className="text-muted-foreground text-sm">
          Construction scope phrases the AI couldn't confidently map to a canonical item — review each one and
          decide whether the engine needs a new item or rule. The AI only detects and logs; it never edits its
          own logic.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Pending Review</p>
              <p className="text-2xl font-bold">{data?.pendingCount ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Total Logged</p>
              <p className="text-2xl font-bold">{data?.total ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Flagged Phrases</CardTitle>
          <CardDescription>Every phrase the engine self-reported as unmapped during generation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | GapStatus)}>
            <SelectTrigger className="w-44 h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : gaps.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No vocabulary gaps match this filter</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phrase</TableHead>
                    <TableHead className="hidden md:table-cell">Context</TableHead>
                    <TableHead>Market</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Logged</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gaps.map((gap) => (
                    <TableRow key={gap.id}>
                      <TableCell className="max-w-[220px] cursor-pointer" onClick={() => setViewGap(gap)}>
                        <p className="font-medium text-sm truncate text-primary hover:underline" title="Click to view full details">{gap.phrase}</p>
                        {gap.adminNote && <p className="text-xs text-muted-foreground mt-1 truncate" title={gap.adminNote}>Note: {gap.adminNote}</p>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell max-w-[280px] cursor-pointer" onClick={() => setViewGap(gap)}>
                        <p className="text-xs text-muted-foreground truncate hover:text-foreground transition-colors" title="Click to view full details">{gap.context ?? "—"}</p>
                      </TableCell>
                      <TableCell className="text-xs uppercase">{gap.market ?? "—"}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] ${statusBadgeClass(gap.status)}`}>{gap.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(gap.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        {gap.status === "pending" ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              onClick={() => setReviewTarget({ gap, action: "approved" })}
                            >
                              <Check className="h-3.5 w-3.5" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs gap-1"
                              onClick={() => setReviewTarget({ gap, action: "dismissed" })}
                            >
                              <X className="h-3.5 w-3.5" /> Dismiss
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {gap.reviewedAt ? formatDateTime(gap.reviewedAt) : "—"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReviewDialog
        gap={reviewTarget?.gap ?? null}
        action={reviewTarget?.action ?? null}
        onClose={() => setReviewTarget(null)}
      />
      <GapDetailDialog gap={viewGap} onClose={() => setViewGap(null)} />
    </div>
  );
}
